import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  createStaffAccount,
  grantAdministrativeRole,
  grantInstitutionalRole,
  revokeAdministrativeRole,
  revokeInstitutionalRole,
  type AccessTarget,
  type AccessTargetInput,
  type AdministrativeRole,
} from "../api/accessManagement";
import { ApiError, NetworkError } from "../api/client";
import type { ElectedRole } from "../api/members";
import { useAuth } from "../auth/AuthContext";
import { useCredentialRevealGuard } from "../auth/CredentialRevealGuard";
import { Button } from "../components/Button";
import { CredentialRevealPanel } from "../components/credentials/CredentialRevealPanel";
import { StatusBanner } from "../components/StatusBanner";
import { TextField } from "../components/TextField";
import { AccessTargetPicker } from "../components/access/AccessTargetPicker";
import { NotFoundPage } from "./NotFoundPage";

interface StaffResult {
  username: string;
  temporary_password: string;
}

const administrativeRoles: Array<{ value: AdministrativeRole; label: string }> = [
  { value: "admin_tecnico", label: "Admin técnico" },
  { value: "marketing", label: "Marketing" },
  { value: "secretaria_daf", label: "Secretaria/DAF" },
];

const electedRoles: Array<{ value: ElectedRole; label: string }> = [
  { value: "diretoria", label: "Diretoria" },
  { value: "conselho_deliberativo", label: "Conselho Deliberativo" },
  { value: "conselho_fiscal", label: "Conselho Fiscal" },
];

function messageFromError(cause: unknown, fallback: string): string {
  return cause instanceof ApiError || cause instanceof NetworkError ? cause.message : fallback;
}

// This page composes three independent operational forms; the measured complexity is presentation
// branching, while each mutation path remains explicit and delegates authorization to its RPC.
// eslint-disable-next-line complexity
export function AccessManagementPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { state, expireSession } = useAuth();
  const { canLeave, clearReveal, guardNavigation } = useCredentialRevealGuard();
  const initialMemberId = useMemo(() => params.get("member_id")?.trim() ?? "", [params]);
  const [administrativeUsernamePrefill, setAdministrativeUsernamePrefill] = useState<string | null>(null);
  const electedInitialTarget = useMemo<AccessTargetInput | undefined>(
    () => initialMemberId ? { member_id: initialMemberId } : undefined,
    [initialMemberId],
  );
  const administrativeInitialTarget = useMemo<AccessTargetInput | undefined>(
    () => administrativeUsernamePrefill
      ? { username: administrativeUsernamePrefill }
      : electedInitialTarget,
    [administrativeUsernamePrefill, electedInitialTarget],
  );
  const [staffName, setStaffName] = useState("");
  const [staffEmail, setStaffEmail] = useState("");
  const [staffResult, setStaffResult] = useState<StaffResult | null>(null);
  const [staffError, setStaffError] = useState<string | null>(null);
  const [savingStaff, setSavingStaff] = useState(false);
  const [electedTarget, setElectedTarget] = useState<AccessTarget | null>(null);
  const [electedRole, setElectedRole] = useState<ElectedRole | "">("");
  const [electedTargetRefresh, setElectedTargetRefresh] = useState(0);
  const [decisionReference, setDecisionReference] = useState("");
  const [roleNotice, setRoleNotice] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState<"grant" | "revoke" | null>(null);
  const [administrativeTarget, setAdministrativeTarget] = useState<AccessTarget | null>(null);
  const [administrativeRole, setAdministrativeRole] = useState<AdministrativeRole | "">("");
  const [administrativeTargetRefresh, setAdministrativeTargetRefresh] = useState(0);
  const [adminDecisionReference, setAdminDecisionReference] = useState("");
  const [adminNotice, setAdminNotice] = useState<string | null>(null);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [savingAdminRole, setSavingAdminRole] = useState<"grant" | "revoke" | null>(null);

  function requestMfaElevation() {
    navigate(`/mfa/desafio?retorno=${encodeURIComponent(location.pathname)}`);
  }

  useEffect(() => () => clearReveal(), [clearReveal]);
  if (state.status !== "authenticated") return null;
  if (state.sessionContextStatus === "loading") {
    return <main className="operational-page"><p className="members-state" role="status">Carregando permissões…</p></main>;
  }
  if (!state.sessionContext?.capabilities.manage_access) return <NotFoundPage />;

  const accessToken = state.accessToken;
  const electedRoleIsActive = Boolean(electedRole && electedTarget?.active_roles.includes(electedRole));
  const electedOperationReady = Boolean(
    electedTarget?.kind === "member" && electedRole && decisionReference.trim() && !savingRole,
  );
  const canGrantRole = Boolean(
    electedOperationReady && electedTarget?.elected_eligibility.eligible && !electedRoleIsActive,
  );
  const canRevokeRole = electedOperationReady && electedRoleIsActive;
  const administrativeRoleIsActive = Boolean(
    administrativeRole && administrativeTarget?.active_roles.includes(administrativeRole),
  );
  const administrativeOperationReady = Boolean(
    administrativeTarget && administrativeRole && !savingAdminRole,
  );
  const canGrantAdministrativeRole = administrativeOperationReady && !administrativeRoleIsActive;
  const canRevokeAdministrativeRole = administrativeOperationReady && administrativeRoleIsActive;

  function handleElectedTargetChange(target: AccessTarget | null) {
    setElectedTarget(target);
    setElectedRole("");
    setDecisionReference("");
    setRoleNotice(null);
    setRoleError(null);
  }

  function handleAdministrativeTargetChange(target: AccessTarget | null) {
    setAdministrativeTarget(target);
    setAdministrativeRole("");
    setAdminDecisionReference("");
    setAdminNotice(null);
    setAdminError(null);
  }

  async function submitStaff(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStaffError(null);
    setStaffResult(null);
    if (!staffName.trim() || !staffEmail.trim()) {
      setStaffError("Informe nome e e-mail antes de criar a conta.");
      return;
    }
    setSavingStaff(true);
    try {
      const response = await createStaffAccount(accessToken, {
        name: staffName.trim(),
        email: staffEmail.trim(),
      });
      setStaffResult({ username: response.username, temporary_password: response.temporary_password });
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        expireSession();
        return;
      }
      if (cause instanceof ApiError && cause.code === "mfa_elevation_required") {
        requestMfaElevation();
        return;
      }
      setStaffError(messageFromError(cause, "Não foi possível criar a conta de funcionário."));
    } finally {
      setSavingStaff(false);
    }
  }

  // eslint-disable-next-line complexity
  async function submitRole(operation: "grant" | "revoke") {
    if (electedTarget?.kind !== "member" || !electedRole) return;
    const roleLabel = electedRoles.find((role) => role.value === electedRole)?.label ?? electedRole;
    if (!window.confirm(`${operation === "grant" ? "Conceder" : "Revogar"} ${roleLabel} para ${electedTarget.display_name}?`)) return;

    setRoleError(null);
    setRoleNotice(null);
    setSavingRole(operation);
    try {
      const input = {
        member_id: electedTarget.member_id,
        role: electedRole,
        decision_reference: decisionReference.trim(),
      };
      await (operation === "grant" ? grantInstitutionalRole(accessToken, input) : revokeInstitutionalRole(accessToken, input));
      setRoleNotice(operation === "grant" ? "Cargo eletivo concedido e estado atualizado." : "Cargo eletivo revogado e estado atualizado.");
      setElectedTarget((current) => current && ({
        ...current,
        active_roles: operation === "grant"
          ? [...new Set([...current.active_roles, electedRole])]
          : current.active_roles.filter((role) => role !== electedRole),
      }));
      setElectedTargetRefresh((current) => current + 1);
      setElectedRole("");
      setDecisionReference("");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        expireSession();
        return;
      }
      if (cause instanceof ApiError && cause.code === "mfa_elevation_required") {
        requestMfaElevation();
        return;
      }
      setRoleError(messageFromError(cause, "Não foi possível registrar a operação de cargo eletivo."));
    } finally {
      setSavingRole(null);
    }
  }

  // eslint-disable-next-line complexity
  async function submitAdministrativeRole(operation: "grant" | "revoke") {
    if (!administrativeTarget || !administrativeRole) return;
    const roleLabel = administrativeRoles.find((role) => role.value === administrativeRole)?.label ?? administrativeRole;
    if (!window.confirm(`${operation === "grant" ? "Conceder" : "Revogar"} ${roleLabel} para ${administrativeTarget.display_name}?`)) return;

    setAdminError(null);
    setAdminNotice(null);
    setSavingAdminRole(operation);
    try {
      const input = {
        ...(administrativeTarget?.kind === "member"
          ? { member_id: administrativeTarget.member_id }
          : { username: administrativeTarget?.username ?? "" }),
        role: administrativeRole,
        decision_reference: adminDecisionReference.trim() || null,
      };
      await (operation === "grant" ? grantAdministrativeRole(accessToken, input) : revokeAdministrativeRole(accessToken, input));
      setAdminNotice(operation === "grant" ? "Designação administrativa concedida." : "Designação administrativa revogada.");
      setAdministrativeTarget((current) => current && ({
        ...current,
        active_roles: operation === "grant"
          ? [...new Set([...current.active_roles, administrativeRole])]
          : current.active_roles.filter((role) => role !== administrativeRole),
      }));
      setAdministrativeTargetRefresh((current) => current + 1);
      setAdministrativeRole("");
      setAdminDecisionReference("");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        expireSession();
        return;
      }
      if (cause instanceof ApiError && cause.code === "mfa_elevation_required") {
        requestMfaElevation();
        return;
      }
      setAdminError(messageFromError(cause, "Não foi possível registrar a designação administrativa."));
    } finally {
      setSavingAdminRole(null);
    }
  }

  return (
    <main className="access-management-page operational-page">
      <Link className="page-back" to="/dashboard" onClick={guardNavigation}>Sair da gestão de acessos</Link>
      <header className="access-management-page__intro">
        <p className="members-page__eyebrow">Administração técnica</p>
        <h1>Gestão de acessos</h1>
        <p>Selecione a pessoa ou conta antes de conceder ou revogar uma designação.</p>
      </header>

      <div className="access-management-grid">
        <section className="form-card access-management-card" aria-labelledby="staff-create-title">
          <p className="members-page__eyebrow">Funcionários</p>
          <h2 id="staff-create-title">Criar funcionário</h2>
          <p className="form-card__intro">Cria uma conta técnica sem conceder papel. A senha temporária aparece apenas nesta tela; qualquer designação é registrada em operação separada.</p>
          {staffError && <StatusBanner tone="error">{staffError}</StatusBanner>}
          {staffResult ? (
            <>
              <CredentialRevealPanel username={staffResult.username} password={staffResult.temporary_password} />
              <Button type="button" variant="secondary" onClick={() => setAdministrativeUsernamePrefill(staffResult.username)}>
                Usar este username para designar papel
              </Button>
              <StatusBanner tone="warning">Entregue a senha por canal seguro. Ela não será persistida nesta tela após sair.</StatusBanner>
              <Button type="button" variant="secondary" disabled={!canLeave} onClick={() => { setStaffResult(null); setStaffName(""); setStaffEmail(""); clearReveal(); }}>
                Criar outro funcionário
              </Button>
            </>
          ) : (
            <form onSubmit={submitStaff} noValidate>
              <TextField label="Nome do funcionário" required value={staffName} onChange={(event) => setStaffName(event.target.value)} />
              <TextField label="E-mail institucional" required type="email" value={staffEmail} onChange={(event) => setStaffEmail(event.target.value)} />
              <Button type="submit" loading={savingStaff}>Criar funcionário</Button>
            </form>
          )}
        </section>

        <section className="form-card access-management-card" aria-labelledby="elected-role-title">
          <p className="members-page__eyebrow">Governança</p>
          <h2 id="elected-role-title">Cargo eletivo do associado</h2>
          <p className="form-card__intro">Concede ou revoga Diretoria e Conselhos usando a referência formal da decisão.</p>
          {roleNotice && <StatusBanner tone="success">{roleNotice}</StatusBanner>}
          {roleError && <StatusBanner tone="error">{roleError}</StatusBanner>}
          <AccessTargetPicker
            key={`elected-target-${electedTargetRefresh}`}
            accessToken={accessToken}
            label="Associado alvo"
            initialTarget={electedTarget?.kind === "member"
              ? { member_id: electedTarget.member_id }
              : electedInitialTarget}
            memberOnly
            onTargetChange={handleElectedTargetChange}
          />
          <label className="select-field">
            <span>Cargo eletivo</span>
            <select value={electedRole} onChange={(event) => setElectedRole(event.target.value as ElectedRole)}>
              <option value="">Selecione um cargo</option>
              {electedRoles.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
            </select>
          </label>
          <TextField label="Referência da decisão" required value={decisionReference} onChange={(event) => setDecisionReference(event.target.value)} placeholder="Ex.: Ata 12/2026" />
          <div className="form-actions">
            <Button type="button" variant="secondary" disabled={!canRevokeRole} loading={savingRole === "revoke"} onClick={() => void submitRole("revoke")}>Revogar cargo eletivo</Button>
            <Button type="button" disabled={!canGrantRole} loading={savingRole === "grant"} onClick={() => void submitRole("grant")}>Conceder cargo eletivo</Button>
          </div>
        </section>

        <section className="form-card access-management-card" aria-labelledby="admin-role-title">
          <p className="members-page__eyebrow">Designação administrativa</p>
          <h2 id="admin-role-title">Designar papel administrativo</h2>
          <p className="form-card__intro">Concede ou revoga Admin técnico, Marketing e Secretaria/DAF para associado ou conta técnica. A referência formal é opcional.</p>
          {adminNotice && <StatusBanner tone="success">{adminNotice}</StatusBanner>}
          {adminError && <StatusBanner tone="error">{adminError}</StatusBanner>}
          <AccessTargetPicker
            key={`administrative-target-${administrativeTargetRefresh}`}
            accessToken={accessToken}
            label="Alvo da designação administrativa"
            initialTarget={administrativeTarget?.kind === "member"
              ? { member_id: administrativeTarget.member_id }
              : administrativeTarget?.kind === "technical"
                ? { username: administrativeTarget.username }
                : administrativeInitialTarget}
            onTargetChange={handleAdministrativeTargetChange}
          />
          <label className="select-field">
            <span>Papel administrativo</span>
            <select value={administrativeRole} onChange={(event) => setAdministrativeRole(event.target.value as AdministrativeRole)}>
              <option value="">Selecione um papel</option>
              {administrativeRoles.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
            </select>
          </label>
          <TextField label="Referência da decisão administrativa" value={adminDecisionReference} onChange={(event) => setAdminDecisionReference(event.target.value)} placeholder="Ex.: Portaria 03/2026" />
          <div className="form-actions">
            <Button type="button" variant="secondary" disabled={!canRevokeAdministrativeRole} loading={savingAdminRole === "revoke"} onClick={() => void submitAdministrativeRole("revoke")}>Revogar designação</Button>
            <Button type="button" disabled={!canGrantAdministrativeRole} loading={savingAdminRole === "grant"} onClick={() => void submitAdministrativeRole("grant")}>Conceder designação</Button>
          </div>
        </section>
      </div>
    </main>
  );
}
